import React from 'react';
import { getAdminDrivers, setPassengerActive } from '../../services/admin';
import { assignDriverToFleet, deactivateDriver, getDrivers, getOpsAuthority, inviteDriver, reactivateDriver } from '../../services/fleet';
import { Role } from '../../types/app';

const PAGE_SIZE = 50;

type DriverRecord = {
  id: string;
  dailymoveId: string;
  initials: string;
  name: string;
  email: string;
  phone: string;
  status: string;
  accountType: string;
  licenceNumber: string;
  licenceExpiry: string;
  company: string;
  vehicle: string;
  rating: number | null;
  trips: number;
  joined: string;
};

export function Drivers({ role = '', searchQuery = '' }: { role?: Role | ''; searchQuery?: string }) {
  const isAdmin = role === 'admin' || role === 'super_admin';
  const [drivers, setDrivers] = React.useState<DriverRecord[]>([]);
  const [selectedDriver, setSelectedDriver] = React.useState<DriverRecord | null>(null);
  const [deactivatingDriver, setDeactivatingDriver] = React.useState<DriverRecord | null>(null);
  const [deactivationReason, setDeactivationReason] = React.useState('');
  const [deactivationNotes, setDeactivationNotes] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [accountType] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState(searchQuery);
  const [offset, setOffset] = React.useState(0);
  const [total, setTotal] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(true);
  const [activeDriverId, setActiveDriverId] = React.useState('');
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [showInvite, setShowInvite] = React.useState(false);
  const [inviteDmId, setInviteDmId] = React.useState('');
  const [isInviting, setIsInviting] = React.useState(false);
  const [inviteError, setInviteError] = React.useState('');
  const [assigningDriver, setAssigningDriver] = React.useState<DriverRecord | null>(null);
  const [assignFleetId, setAssignFleetId] = React.useState('');
  const [assignFleets, setAssignFleets] = React.useState<{ id: string; name: string }[]>([]);
  const [isAssigning, setIsAssigning] = React.useState(false);
  const [assignError, setAssignError] = React.useState('');

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setOffset(0);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  React.useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    setError('');

    const request = isAdmin
      ? getAdminDrivers({ status, accountType, search: debouncedSearch, limit: PAGE_SIZE, offset })
      : getDrivers();

    request
      .then((response) => {
        if (isActive) {
          const records = normalizeDrivers(response);
          setDrivers(records);
          setTotal(readNestedNumber(response, ['total', 'count', 'drivers_total', 'total_drivers']) ?? records.length);
        }
      })
      .catch((requestError) => {
        if (isActive) {
          setDrivers([]);
          setError(requestError instanceof Error ? requestError.message : 'Unable to load drivers');
        }
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [accountType, debouncedSearch, isAdmin, offset, status]);

  React.useEffect(() => {
    if (!selectedDriver && !deactivatingDriver) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedDriver(null);
        setDeactivatingDriver(null);
      }
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [deactivatingDriver, selectedDriver]);

  const pageStart = total ? offset + 1 : 0;
  const pageEnd = Math.min(offset + drivers.length, total);
  const statusCounts = {
    all: status === '' ? total : drivers.length,
    active: drivers.filter((driver) => driver.status === 'active').length,
    inactive: drivers.filter((driver) => driver.status === 'inactive').length,
    pending: drivers.filter((driver) => driver.status === 'pending').length,
  };

  // Fleet-manager: client-side filter by navbar search query
  const visibleDrivers = isAdmin
    ? drivers
    : drivers.filter((driver) => {
        if (!debouncedSearch.trim()) return true;
        const q = debouncedSearch.toLowerCase();
        return [driver.name, driver.phone, driver.email, driver.dailymoveId, driver.licenceNumber].some(
          (field) => field?.toLowerCase().includes(q),
        );
      });

  const updateDriverStatus = async (
    driver: DriverRecord,
    isActive: boolean,
    details?: { reason: string; notes: string },
  ) => {
    setActiveDriverId(driver.id);
    setError('');
    setMessage('');

    try {
      if (isAdmin) {
        await setPassengerActive(driver.id, isActive, details);
      } else if (isActive) {
        await reactivateDriver(driver.id);
      } else {
        await deactivateDriver(driver.id);
      }
      const nextStatus = isActive ? 'active' : 'inactive';
      setDrivers((current) => current.map((item) => item.id === driver.id ? { ...item, status: nextStatus } : item));
      setSelectedDriver((current) => current?.id === driver.id ? { ...current, status: nextStatus } : current);
      setDeactivatingDriver(null);
      setDeactivationReason('');
      setDeactivationNotes('');
      setMessage(`${driver.name} was ${isActive ? 'activated' : 'deactivated'}.`);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to update driver status');
    } finally {
      setActiveDriverId('');
    }
  };

  const openInvite = () => {
    setShowInvite(true);
    setInviteDmId('');
    setInviteError('');
  };

  const submitInvite = async () => {
    const driverDailymoveId = inviteDmId.trim();
    if (!driverDailymoveId) { setInviteError("Enter the driver's DailyMove ID"); return; }
    setIsInviting(true);
    setInviteError('');
    try {
      await inviteDriver(driverDailymoveId);
      setShowInvite(false);
      setMessage('Invitation sent — driver will join your pool once they accept.');
    } catch (requestError) {
      setInviteError(requestError instanceof Error ? requestError.message : 'Could not send invitation');
    } finally {
      setIsInviting(false);
    }
  };

  const openAssign = (driver: DriverRecord) => {
    setAssigningDriver(driver);
    setAssignFleetId('');
    setAssignError('');
    getOpsAuthority()
      .then((response) => setAssignFleets(normalizeFleetOptions(response)))
      .catch(() => setAssignFleets([]));
  };

  const submitAssign = async () => {
    if (!assigningDriver) return;
    if (!assignFleetId) { setAssignError('Select a fleet'); return; }
    setIsAssigning(true);
    setAssignError('');
    try {
      await assignDriverToFleet(assigningDriver.id, assignFleetId);
      setAssigningDriver(null);
      setMessage(`${assigningDriver.name} was assigned to a fleet.`);
    } catch (requestError) {
      setAssignError(requestError instanceof Error ? requestError.message : 'Could not assign driver to fleet');
    } finally {
      setIsAssigning(false);
    }
  };

  const exportCsv = () => {
    const rows = [
      ['Driver', 'Phone', 'Licence', 'Vehicle', 'Rating', 'Status'],
      ...drivers.map((driver) => [
        driver.name,
        driver.phone,
        driver.licenceNumber,
        driver.vehicle,
        driver.rating?.toFixed(1) ?? '',
        driver.status,
      ]),
    ];
    const csv = rows.map((row) => row.map(csvCell).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'drivers.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={`drivers-page${isAdmin ? ' admin-drivers-page' : ''}`}>
      <div className="section-head drivers-head">
        <div>
          <h2>{isAdmin ? 'All drivers' : 'Drivers'}</h2>
          {!isAdmin ? <span>{total.toLocaleString()} driver{total === 1 ? '' : 's'}</span> : null}
        </div>
        {isAdmin ? (
          <button className="btn admin-driver-export" disabled={!drivers.length} type="button" onClick={exportCsv}>Export CSV</button>
        ) : (
          <button className="btn btn-primary" type="button" onClick={openInvite}><PlusIcon /> Invite driver</button>
        )}
      </div>

      {isAdmin ? (
        <div className="admin-driver-tabs" role="tablist" aria-label="Driver status">
          {[
            { label: 'All', value: '', count: statusCounts.all },
            { label: 'Active', value: 'active', count: statusCounts.active },
            { label: 'Suspended', value: 'inactive', count: statusCounts.inactive },
            { label: 'Pending KYC', value: 'pending', count: statusCounts.pending },
          ].map((tab) => (
            <button
              aria-selected={status === tab.value}
              className={status === tab.value ? 'active' : ''}
              key={tab.label}
              role="tab"
              type="button"
              onClick={() => { setStatus(tab.value); setOffset(0); }}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
      ) : null}

      {error ? <div className="kyc-message passengers-error" role="alert">{error}</div> : null}
      {message ? <div className="kyc-message" role="status">{message}</div> : null}

      <div className="data-table drivers-table">
        <table>
          <thead>
            <tr>
              <th>Driver</th>
              <th>DailyMove ID</th>
              <th>Licence</th>
              <th>Vehicle</th>
              <th>Rating</th>
              <th>Status</th>
              <th><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td className="kyc-table-state" colSpan={7}>Loading drivers...</td></tr>
            ) : visibleDrivers.length ? visibleDrivers.map((driver) => (
              <tr key={driver.id}>
                <td data-label="Driver">
                  <div className="row-cell">
                    <span className="row-avatar">{driver.initials}</span>
                    <span className="row-info"><strong>{driver.name}</strong><span>{driver.phone}</span></span>
                  </div>
                </td>
                <td data-label="DailyMove ID"><span className="kyc-document">{driver.dailymoveId}</span></td>
                <td data-label="Licence"><span className="kyc-document">{driver.licenceNumber}</span></td>
                <td data-label="Vehicle">{driver.vehicle}</td>
                <td data-label="Rating"><span className="driver-rating"><StarIcon /> {driver.rating?.toFixed(1) ?? '—'}</span></td>
                <td data-label="Status"><span className={`pill ${statusTone(driver.status)}`}>{driver.status === 'inactive' ? 'suspended' : driver.status}</span></td>
                <td>
                  <div className="admin-driver-actions">
                    <button
                      aria-label={`View ${driver.name}`}
                      className="btn-icon borderless passenger-view-btn"
                      title="View driver"
                      type="button"
                      onClick={() => setSelectedDriver(driver)}
                    >
                      <EyeIcon />
                    </button>
                    {isAdmin ? (
                      <button
                        aria-label={driver.status === 'active' ? `Deactivate ${driver.name}` : `Activate ${driver.name}`}
                        className="btn-icon borderless"
                        disabled={activeDriverId === driver.id}
                        title={driver.status === 'active' ? 'Deactivate driver' : 'Activate driver'}
                        type="button"
                        onClick={() => driver.status === 'active' ? setDeactivatingDriver(driver) : updateDriverStatus(driver, true)}
                      >
                        {driver.status === 'active' ? <DeactivateIcon /> : <PlusIcon />}
                      </button>
                    ) : (
                      <button
                        aria-label={`Assign ${driver.name} to a fleet`}
                        className="btn-icon borderless"
                        title="Assign to fleet"
                        type="button"
                        onClick={() => openAssign(driver)}
                      >
                        <BranchIcon />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )) : (
              <tr><td className="kyc-table-state" colSpan={7}>{debouncedSearch && !isAdmin ? `No drivers match "${debouncedSearch}".` : 'No drivers found.'}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {isAdmin ? (
        <div className="passengers-pagination">
          <span>Showing {pageStart}-{pageEnd} of {total.toLocaleString()}</span>
          <div>
            <button className="btn" disabled={offset === 0 || isLoading} type="button" onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</button>
            <button className="btn" disabled={offset + PAGE_SIZE >= total || isLoading} type="button" onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button>
          </div>
        </div>
      ) : null}

      {selectedDriver ? (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setSelectedDriver(null)}>
          <section aria-labelledby="driver-details-title" aria-modal="true" className="signup-dialog driver-details-dialog" role="dialog" onMouseDown={(event) => event.stopPropagation()}>
            <div className="signup-dialog-head">
              <div>
                <span className="row-avatar">{selectedDriver.initials}</span>
                <div><h2 id="driver-details-title">{selectedDriver.name}</h2><span className={`pill ${statusTone(selectedDriver.status)}`}>{selectedDriver.status}</span></div>
              </div>
              <button className="btn-icon borderless" type="button" aria-label="Close driver details" onClick={() => setSelectedDriver(null)}><span aria-hidden="true">&times;</span></button>
            </div>
            <dl className="kyc-details">
              <DriverDetail label="DailyMove ID" value={selectedDriver.dailymoveId} document />
              <DriverDetail label="Email" value={selectedDriver.email} />
              <DriverDetail label="Phone" value={selectedDriver.phone} />
              <DriverDetail label="Account type" value={selectedDriver.accountType} />
              <DriverDetail label="Licence number" value={selectedDriver.licenceNumber} document />
              <DriverDetail label="Licence expiry" value={selectedDriver.licenceExpiry} />
              <DriverDetail label="Company" value={selectedDriver.company} />
              <DriverDetail label="Vehicle" value={selectedDriver.vehicle} />
              <DriverDetail label="Rating" value={selectedDriver.rating?.toFixed(1) ?? 'Not rated'} />
              <DriverDetail label="Total trips" value={selectedDriver.trips.toLocaleString()} />
              <DriverDetail label="Joined" value={selectedDriver.joined} />
              <DriverDetail label="Driver ID" value={selectedDriver.id} document />
            </dl>
            <div className="kyc-dialog-actions">
              <button className="btn" type="button" onClick={() => setSelectedDriver(null)}>Close</button>
              {isAdmin ? (
                <button
                  className={selectedDriver.status === 'active' ? 'btn btn-danger' : 'btn btn-primary'}
                  disabled={activeDriverId === selectedDriver.id}
                  type="button"
                  onClick={() => selectedDriver.status === 'active' ? setDeactivatingDriver(selectedDriver) : updateDriverStatus(selectedDriver, true)}
                >
                  {selectedDriver.status === 'active' ? 'Deactivate driver' : 'Activate driver'}
                </button>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}

      {deactivatingDriver ? (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setDeactivatingDriver(null)}>
          <form
            aria-labelledby="deactivate-driver-title"
            aria-modal="true"
            className="signup-dialog passenger-deactivate-dialog"
            role="dialog"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={(event) => {
              event.preventDefault();
              updateDriverStatus(deactivatingDriver, false, {
                reason: deactivationReason.trim(),
                notes: deactivationNotes.trim(),
              });
            }}
          >
            <div className="signup-dialog-head">
              <div>
                <span className="row-avatar">{deactivatingDriver.initials}</span>
                <div><h2 id="deactivate-driver-title">Deactivate driver</h2><span>{deactivatingDriver.name}</span></div>
              </div>
              <button className="btn-icon borderless" type="button" aria-label="Close deactivation dialog" onClick={() => setDeactivatingDriver(null)}><span aria-hidden="true">&times;</span></button>
            </div>
            <div className="form-group">
              <label htmlFor="driver-deactivation-reason">Reason</label>
              <input autoFocus id="driver-deactivation-reason" placeholder="Reason for deactivation" required value={deactivationReason} onChange={(event) => setDeactivationReason(event.target.value)} />
            </div>
            <div className="form-group">
              <label htmlFor="driver-deactivation-notes">Notes</label>
              <textarea id="driver-deactivation-notes" placeholder="Additional notes (optional)" value={deactivationNotes} onChange={(event) => setDeactivationNotes(event.target.value)} />
            </div>
            <div className="kyc-dialog-actions">
              <button className="btn" type="button" onClick={() => setDeactivatingDriver(null)}>Cancel</button>
              <button className="btn btn-danger" disabled={!deactivationReason.trim() || activeDriverId === deactivatingDriver.id} type="submit">Deactivate driver</button>
            </div>
          </form>
        </div>
      ) : null}

      {showInvite ? (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setShowInvite(false)}>
          <form
            aria-labelledby="invite-driver-title"
            aria-modal="true"
            className="signup-dialog"
            role="dialog"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={(event) => { event.preventDefault(); submitInvite(); }}
          >
            <div className="signup-dialog-head">
              <div><h2 id="invite-driver-title">Invite driver</h2></div>
              <button aria-label="Close invite dialog" className="btn-icon borderless" type="button" onClick={() => setShowInvite(false)}><span aria-hidden="true">&times;</span></button>
            </div>
            <p className="muted">Enter the driver's DailyMove ID to invite them into your driver pool. Once they accept, you can assign them to any of your fleets.</p>
            {inviteError ? <div className="kyc-message passengers-error" role="alert">{inviteError}</div> : null}
            <div className="form-group">
              <label htmlFor="invite-driver-dmid">Driver DailyMove ID</label>
              <input autoFocus id="invite-driver-dmid" placeholder="e.g. DM-12345" value={inviteDmId} onChange={(event) => setInviteDmId(event.target.value)} />
            </div>
            <div className="kyc-dialog-actions">
              <button className="btn" type="button" onClick={() => setShowInvite(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={isInviting} type="submit">{isInviting ? 'Sending...' : 'Send invitation'}</button>
            </div>
          </form>
        </div>
      ) : null}

      {assigningDriver ? (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setAssigningDriver(null)}>
          <form
            aria-labelledby="assign-driver-title"
            aria-modal="true"
            className="signup-dialog"
            role="dialog"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={(event) => { event.preventDefault(); submitAssign(); }}
          >
            <div className="signup-dialog-head">
              <div><h2 id="assign-driver-title">Assign {assigningDriver.name} to a fleet</h2></div>
              <button aria-label="Close assign dialog" className="btn-icon borderless" type="button" onClick={() => setAssigningDriver(null)}><span aria-hidden="true">&times;</span></button>
            </div>
            <p className="muted">Choose which of your fleets this driver should be assigned to.</p>
            {assignError ? <div className="kyc-message passengers-error" role="alert">{assignError}</div> : null}
            <div className="form-group">
              <label htmlFor="assign-driver-fleet">Fleet</label>
              <select id="assign-driver-fleet" value={assignFleetId} onChange={(event) => setAssignFleetId(event.target.value)}>
                <option value="">Select a fleet</option>
                {assignFleets.map((fleet) => <option key={fleet.id} value={fleet.id}>{fleet.name}</option>)}
              </select>
            </div>
            <div className="kyc-dialog-actions">
              <button className="btn" type="button" onClick={() => setAssigningDriver(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={isAssigning} type="submit">{isAssigning ? 'Assigning...' : 'Assign'}</button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}

function normalizeFleetOptions(response: unknown): { id: string; name: string }[] {
  const records: Record<string, unknown>[] = [];
  const collect = (value: unknown) => {
    if (Array.isArray(value)) { value.forEach(collect); return; }
    if (!isObject(value)) return;
    const hasId = readString(value, 'id', 'fleet_id', 'group_id');
    const type = readString(value, 'type', 'group_type', 'kind');
    if (hasId && (!type || type === 'fleet')) records.push(value);
    for (const key of ['authority', 'fleets', 'groups', 'items', 'results', 'children', 'data']) {
      if (key in value) collect(value[key]);
    }
  };
  collect(response);
  return records.map((record, index) => ({
    id: readString(record, 'id', 'fleet_id', 'group_id') ?? String(index),
    name: readString(record, 'name', 'fleet_name', 'group_name') ?? 'Unnamed fleet',
  }));
}

function normalizeDrivers(response: unknown): DriverRecord[] {
  return getDriverSource(response).map((driver, index) => {
    const user = readObject(driver, 'user') ?? {};
    const profile = readObject(driver, 'profile') ?? readObject(driver, 'driver_profile') ?? {};
    const name = readString(driver, 'full_name', 'name', 'username') ?? readString(user, 'full_name', 'name', 'username', 'email') ?? 'Unknown driver';
    return {
      id: readString(driver, 'user_id', 'id', 'driver_id') ?? readString(user, 'id', 'user_id') ?? String(index),
      dailymoveId: readString(driver, 'driver_dailymove_id', 'dailymove_id') ?? readString(user, 'driver_dailymove_id', 'dailymove_id') ?? 'Not provided',
      initials: getInitials(name),
      name,
      email: readString(driver, 'email') ?? readString(user, 'email') ?? 'No email address',
      phone: readString(driver, 'phone_number', 'phone') ?? readString(user, 'phone_number', 'phone') ?? 'No phone number',
      status: getStatus(driver, user),
      accountType: (readString(driver, 'account_type', 'driver_type', 'type') ?? 'independent').replace(/_/g, ' '),
      licenceNumber: readString(driver, 'licence_number', 'license_number') ?? readString(profile, 'licence_number', 'license_number') ?? 'Not provided',
      licenceExpiry: formatDate(readString(driver, 'licence_expiry_date', 'license_expiry_date') ?? readString(profile, 'licence_expiry_date', 'license_expiry_date')),
      company: readString(driver, 'company_name', 'fleet_name') ?? readString(profile, 'company_name', 'fleet_name') ?? 'Independent',
      vehicle: getVehicleLabel(driver),
      rating: readNumber(driver, 'rating', 'average_rating', 'driver_rating') ?? null,
      trips: readNumber(driver, 'trips_total', 'total_trips', 'trip_count', 'trips_count') ?? 0,
      joined: formatDate(readString(driver, 'created_at', 'joined_at') ?? readString(user, 'created_at', 'joined_at')),
    };
  });
}

function getDriverSource(response: unknown): Record<string, unknown>[] {
  if (Array.isArray(response)) return response.filter(isObject);
  if (!isObject(response)) return [];
  for (const key of ['drivers', 'items', 'results', 'records', 'users', 'data']) {
    const value = response[key];
    if (Array.isArray(value)) return value.filter(isObject);
    if (isObject(value)) {
      const drivers = getDriverSource(value);
      if (drivers.length) return drivers;
    }
  }
  return [];
}

function DriverDetail({ document = false, label, value }: { document?: boolean; label: string; value: string }) {
  return <div><dt>{label}</dt><dd className={document ? 'kyc-document' : undefined}>{value}</dd></div>;
}

function EyeIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" /><circle cx="12" cy="12" r="3" /></svg>;
}

function StarIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M12 2.8l2.8 5.7 6.3.9-4.6 4.4 1.1 6.2-5.6-2.9L6.4 20l1.1-6.2-4.6-4.4 6.3-.9L12 2.8z" /></svg>;
}

function DeactivateIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 14h10l1-14M9 7V4h6v3" /></svg>;
}

function BranchIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="6" cy="6" r="3" /><circle cx="18" cy="18" r="3" /><path d="M9 6h6a4 4 0 014 4v2a4 4 0 01-4 4H9" /></svg>;
}

function PlusIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>;
}

function getVehicleLabel(driver: Record<string, unknown>) {
  const vehicle = readObject(driver, 'vehicle') ?? readObject(driver, 'assigned_vehicle');
  return readString(driver, 'vehicle_registration_number', 'vehicle_plate_number', 'registration_number', 'plate_number', 'assigned_vehicle_plate')
    ?? (vehicle ? readString(vehicle, 'registration_number', 'plate_number', 'name') : undefined)
    ?? '—';
}

function csvCell(value: string) {
  return `"${value.replace(/"/g, '""')}"`;
}

function statusTone(status: string) {
  if (status === 'active' || status === 'approved') return 'success';
  if (status === 'inactive' || status === 'rejected') return 'danger';
  return 'warn';
}

function getStatus(driver: Record<string, unknown>, user: Record<string, unknown>) {
  const status = readString(driver, 'status', 'account_status') ?? readString(user, 'status', 'account_status');
  if (status) return status.toLowerCase();
  return readBoolean(driver, 'is_active', 'active') === false || readBoolean(user, 'is_active', 'active') === false ? 'inactive' : 'active';
}

function formatDate(value: string | undefined) {
  if (!value) return 'Unknown';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function getInitials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'NA';
}

function readString(source: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value) return value;
  }
  return undefined;
}

function readNumber(source: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number') return value;
  }
  return undefined;
}

function readBoolean(source: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'boolean') return value;
  }
  return undefined;
}

function readObject(source: Record<string, unknown>, key: string) {
  return isObject(source[key]) ? source[key] as Record<string, unknown> : undefined;
}

function readNestedNumber(response: unknown, keys: string[]): number | undefined {
  if (!isObject(response)) return undefined;
  const direct = readNumber(response, ...keys);
  if (direct !== undefined) return direct;
  for (const value of Object.values(response)) {
    const nested = readNestedNumber(value, keys);
    if (nested !== undefined) return nested;
  }
  return undefined;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
